// Navigation Kernel – centrální orchestrátor navigačního jádra

import { AgendaRegistry } from './agendaRegistry';
import type { AgendaHandle } from './agendaDefinition';
import type { LayerDescriptor } from './layerDescriptor';
import { createLayerDescriptor } from './layerDescriptor';
import type { NavigationState } from './navigationState';
import {
  createInitialNavigationState,
  pushLayer,
  popLayer,
  closeLayersAbove,
  removeLayer,
  replaceWithRoot,
  replaceTopLayer,
  updateLayerState,
  setLayerStackId,
} from './navigationState';
import type { RouterAdapter } from './routerAdapter';
import { serializeStack } from './navigationSerializer';
import type { ParamCodecMap, ParamInput } from './paramCodec';
import { restoreFromUrl } from './restoreFromUrl';

/** Callback pro notifikaci o změně navigačního stavu */
export type NavigationStateListener = (state: NavigationState) => void;

/**
 * Guard registrovaný pro vrstvu – volaný před zavřením vrstvy.
 * Vrací true = povolení zavření, false = zamítnutí (navigace se neprovede).
 */
export type LayerCloseGuard = () => Promise<boolean>;

/** Konfigurace pro vytvoření kernelu */
export interface NavigationKernelConfig {
  readonly router: RouterAdapter;
}

/**
 * Navigation Kernel – factory a orchestrátor navigačního stavu.
 * Drží registr agend, navigační stav a synchronizuje jej s URL.
 */
export class NavigationKernel {
  private readonly registry = new AgendaRegistry();
  private readonly componentMap = new Map<string, React.ComponentType<{ layerId: string }>>();
  private state: NavigationState = createInitialNavigationState();
  private readonly listeners = new Set<NavigationStateListener>();
  private router: RouterAdapter | null = null;
  private unsubscribeRouter: (() => void) | null = null;

  /** Registry close guardů – klíč = layerId, hodnota = async guard funkce */
  private readonly closeGuards = new Map<string, LayerCloseGuard>();

  /** Připojí router adapter a naslouchá externím změnám URL (browser back/forward) */
  connectRouter(router: RouterAdapter): void {
    this.unsubscribeRouter?.();
    this.router = router;
    this.unsubscribeRouter = router.subscribe(() => {
      this.handleExternalUrlChange();
    });
  }

  /** Odpojí router adapter a přestane naslouchat přes subscribe */
  disconnectRouter(): void {
    this.unsubscribeRouter?.();
    this.unsubscribeRouter = null;
    this.router = null;
  }

  /**
   * Reaguje na změnu URL způsobenou externě (browser back/forward).
   */
  private handleExternalUrlChange(): void {
    if (!this.router) return;

    const currentUrlParams = this.router.getSearchParams();
    const expectedUrlParams = serializeStack(this.state.layers, (agendaId) =>
      this.registry.getById(agendaId)
    );

    if (urlParamsEqual(currentUrlParams, expectedUrlParams)) return;
    this.restoreFromCurrentUrl();
  }

  /** Registruje agendu */
  registerAgenda(config: Parameters<AgendaRegistry['register']>[0]): void {
    this.registry.register(config);
  }

  /** Registruje komponentu pro agendu */
  registerComponent(agendaId: string, component: React.ComponentType<{ layerId: string }>): void {
    this.componentMap.set(agendaId, component);
  }

  /** Vrátí registr */
  getRegistry(): AgendaRegistry {
    return this.registry;
  }

  /** Vrátí aktuální stav */
  getState(): NavigationState {
    return this.state;
  }

  // ── Guard registry ──────────────────────────────────────────────────────────

  /**
   * Zaregistruje close guard pro vrstvu.
   * Vrátí funkci pro odregistraci (volat při unmount komponenty).
   */
  registerCloseGuard(layerId: string, guard: LayerCloseGuard): () => void {
    this.closeGuards.set(layerId, guard);
    return () => {
      this.closeGuards.delete(layerId);
    };
  }

  /** Zkontroluje guard pro jednu vrstvu. Pokud guard neexistuje, vrátí true. */
  private async canCloseLayer(layerId: string): Promise<boolean> {
    const guard = this.closeGuards.get(layerId);
    if (!guard) return true;
    return guard();
  }

  /**
   * Zkontroluje guardy pro seznam vrstev (nejnovější první).
   * Při prvním zamítnutí vrátí false a nepokračuje.
   */
  private async canCloseLayers(layerIds: string[]): Promise<boolean> {
    for (const id of [...layerIds].reverse()) {
      if (!(await this.canCloseLayer(id))) return false;
    }
    return true;
  }

  // ── Navigační operace ───────────────────────────────────────────────────────

  /**
   * Otevře nový root – uzavře staré vrstvy PLOCHY (bez `stackId`).
   * Osamostatněná (floating) okna žijí ve vlastních stacích a zůstávají otevřená,
   * proto se jejich guardy nevolají a vrstvy se nezavírají.
   * Před uzavřením zkontroluje guardy vrstev plochy.
   */
  async openRoot<TOpen extends ParamCodecMap>(
    agenda: AgendaHandle<TOpen>,
    input: ParamInput<TOpen>,
    component?: React.ComponentType<{ layerId: string }>
  ): Promise<void> {
    const plochaLayerIds = this.state.layers
      .filter((l) => l.stackId === undefined)
      .map((l) => l.layerId);
    if (!(await this.canCloseLayers(plochaLayerIds))) return;

    const layer = createLayerDescriptor({
      agendaId: agenda.id,
      presentation: 'root',
      openParams: input as Record<string, unknown>,
      component: component ?? this.componentMap.get(agenda.id),
    });
    this.state = replaceWithRoot(this.state, layer);
    this.syncToUrl('push');
    this.notifyListeners();
  }

  /**
   * Otevře modální vrstvu nad aktuálním kontextem.
   * V list-detail režimu při nahrazení detailu zkontroluje guard nahrazované vrstvy.
   */
  async openModal<TOpen extends ParamCodecMap>(
    agenda: AgendaHandle<TOpen>,
    input: ParamInput<TOpen>,
    component?: React.ComponentType<{ layerId: string }>
  ): Promise<void> {
    const layer = createLayerDescriptor({
      agendaId: agenda.id,
      presentation: 'modal',
      openParams: input as Record<string, unknown>,
      component: component ?? this.componentMap.get(agenda.id),
    });

    if (this.shouldReplaceDetailModal(agenda.id)) {
      // Nahrazujeme stávající detail modal – guard musí potvrdit zavření
      const top = this.state.layers[this.state.layers.length - 1];
      if (top && !(await this.canCloseLayer(top.layerId))) return;
      this.state = replaceTopLayer(this.state, layer);
    } else {
      // Otevíráme modal na stack – guard se NEVOLÁ (okno zůstává otevřené)
      this.state = pushLayer(this.state, layer);
    }

    this.syncToUrl('push');
    this.notifyListeners();
  }

  /** Otevře overlay root nad existujícím kontextem */
  async openOverlayRoot<TOpen extends ParamCodecMap>(
    agenda: AgendaHandle<TOpen>,
    input: ParamInput<TOpen>,
    component?: React.ComponentType<{ layerId: string }>
  ): Promise<void> {
    const layer = createLayerDescriptor({
      agendaId: agenda.id,
      presentation: 'root',
      openParams: input as Record<string, unknown>,
      component: component ?? this.componentMap.get(agenda.id),
    });
    this.state = pushLayer(this.state, layer);
    this.syncToUrl('push');
    this.notifyListeners();
  }

  /**
   * Zjistí, zda se v list-detail režimu má nahradit aktuální detail modal.
   */
  private shouldReplaceDetailModal(agendaId: string): boolean {
    const rootLayer = this.state.layers.find((l) => l.presentation === 'root');
    if (!rootLayer) return false;

    const rootConfig = this.registry.getById(rootLayer.agendaId);
    if (!rootConfig) return false;

    const layoutMode = rootConfig.presentationHints?.shell?.layoutMode;
    if (layoutMode !== 'list-detail') return false;

    const allowlist = rootConfig.presentationHints?.shell?.detailAgendaAllowlist ?? [];
    if (!allowlist.includes(agendaId)) return false;

    const top = this.state.layers[this.state.layers.length - 1];
    // Replace jen DOCKED detailu (v pravém panelu). Osamostatněný (floating, stackId) detail
    // se nenahrazuje — klik v listu otevře nový docked detail a floating okno zůstane.
    if (!top || top.presentation !== 'modal' || top.stackId !== undefined) return false;

    return allowlist.includes(top.agendaId);
  }

  /**
   * Nahradí vrchní modální vrstvu novou modální vrstvou (detail replacement).
   * Zkontroluje guard nahrazované vrstvy.
   */
  async replaceModal<TOpen extends ParamCodecMap>(
    agenda: AgendaHandle<TOpen>,
    input: ParamInput<TOpen>,
    component?: React.ComponentType<{ layerId: string }>
  ): Promise<void> {
    const layer = createLayerDescriptor({
      agendaId: agenda.id,
      presentation: 'modal',
      openParams: input as Record<string, unknown>,
      component: component ?? this.componentMap.get(agenda.id),
    });

    const top = this.state.layers[this.state.layers.length - 1];
    if (top?.presentation === 'modal') {
      if (!(await this.canCloseLayer(top.layerId))) return;
      this.state = replaceTopLayer(this.state, layer);
    } else {
      this.state = pushLayer(this.state, layer);
    }

    this.syncToUrl('push');
    this.notifyListeners();
  }

  /**
   * Zavře vrchní vrstvu.
   * Zkontroluje guard vrchní vrstvy.
   */
  async closeTop(): Promise<void> {
    const top = this.state.layers[this.state.layers.length - 1];
    if (top && !(await this.canCloseLayer(top.layerId))) return;
    this.state = popLayer(this.state);
    this.syncToUrl('push');
    this.notifyListeners();
  }

  /**
   * Zavře všechny vrstvy nad danou vrstvou.
   * Zkontroluje guardy všech uzavíraných vrstev.
   */
  async closeTo(layerId: string): Promise<void> {
    const idx = this.state.layers.findIndex((l) => l.layerId === layerId);
    if (idx < 0) return;
    const layersToClose = this.state.layers.slice(idx + 1).map((l) => l.layerId);
    if (!(await this.canCloseLayers(layersToClose))) return;
    this.state = closeLayersAbove(this.state, layerId);
    this.syncToUrl('push');
    this.notifyListeners();
  }

  /**
   * Zavře konkrétní vrstvu.
   * Zkontroluje guard dané vrstvy.
   */
  async closeLayer(layerId: string): Promise<void> {
    if (!(await this.canCloseLayer(layerId))) return;
    this.state = removeLayer(this.state, layerId);
    this.syncToUrl('push');
    this.notifyListeners();
  }

  // ── State operace (synchronní – neovlivňují close guardy) ──────────────────

  /** Aktualizuje state parametry vrstvy */
  patchLayerState(layerId: string, partialState: Record<string, unknown>): void {
    const layer = this.state.layers.find((l) => l.layerId === layerId);
    if (!layer) return;

    const newState = { ...layer.stateParams, ...partialState };
    this.state = updateLayerState(this.state, layerId, newState);
    this.syncToUrl('replace');
    this.notifyListeners();
  }

  /**
   * Nastaví nebo zruší `stackId` vrstvy (osamostatnění / vrácení do plochy).
   * Synchronní operace — neovlivňuje close guardy, jen mění zařazení do stacku.
   * `stackId === undefined` vrátí vrstvu do výchozí plochy.
   */
  setLayerStackId(layerId: string, stackId: string | undefined): void {
    const layer = this.state.layers.find((l) => l.layerId === layerId);
    if (!layer) return;
    this.state = setLayerStackId(this.state, layerId, stackId);
    this.syncToUrl('replace');
    this.notifyListeners();
  }

  /** Resetuje state parametry vrstvy */
  resetLayerState(layerId: string): void {
    this.state = updateLayerState(this.state, layerId, {});
    this.syncToUrl('replace');
    this.notifyListeners();
  }

  /** Odstraní konkrétní state klíče z vrstvy */
  removeLayerStateKeys(layerId: string, keys: string[]): void {
    const layer = this.state.layers.find((l) => l.layerId === layerId);
    if (!layer) return;

    const newState = { ...layer.stateParams };
    for (const key of keys) {
      delete newState[key];
    }
    this.state = updateLayerState(this.state, layerId, newState);
    this.syncToUrl('replace');
    this.notifyListeners();
  }

  /** Nastaví celý stack přímo (pro restore z URL) */
  restoreState(state: NavigationState): void {
    this.state = state;
    this.notifyListeners();
  }

  /** Obnoví stav z aktuální URL */
  restoreFromCurrentUrl(): void {
    if (!this.router) return;
    const searchParams = this.router.getSearchParams();
    const state = restoreFromUrl(searchParams, this.registry);
    this.state = {
      ...state,
      layers: state.layers.map((layer) => {
        const component = this.componentMap.get(layer.agendaId);
        return component ? { ...layer, component } : layer;
      }),
    };
    this.notifyListeners();
  }

  /** Přihlásí listener na změny stavu */
  subscribe(listener: NavigationStateListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Najde vrstvu podle ID */
  getLayer(layerId: string): LayerDescriptor | undefined {
    return this.state.layers.find((l) => l.layerId === layerId);
  }

  private syncToUrl(action: 'push' | 'replace'): void {
    if (!this.router) return;

    const searchParams = serializeStack(this.state.layers, (agendaId) =>
      this.registry.getById(agendaId)
    );
    this.router.setSearchParams(searchParams, action);
  }

  private notifyListeners(): void {
    for (const listener of this.listeners) {
      listener(this.state);
    }
  }
}

/** Factory pro navigační kernel */
export function createNavigationKernel(): NavigationKernel {
  return new NavigationKernel();
}

/** Porovná dva URLSearchParams – order-insensitive */
function urlParamsEqual(a: URLSearchParams, b: URLSearchParams): boolean {
  const sortedA = [...a.entries()].sort(([k1], [k2]) => k1.localeCompare(k2));
  const sortedB = [...b.entries()].sort(([k1], [k2]) => k1.localeCompare(k2));
  if (sortedA.length !== sortedB.length) return false;
  return sortedA.every(([k, v], i) => {
    const entry = sortedB[i];
    return entry !== undefined && entry[0] === k && entry[1] === v;
  });
}
